import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '../../../environments/environment';
import { URL_MAP } from '../../shared/urlMap';

import { NetworkingMyQuotation } from '../../services/mis-cotizaciones/miscotizaciones.service';
import { AuthService } from '../../services/security/auth.service';
import { ConsultarLineasGruposProgramasService } from '../../services/cotizador-programas/consultar-lineas-grupos-programas.service';
import { ProbabilidadCierreService } from '../../services/probabilidad-cierre/probabilidad-cierre.service';
import { CotizacionEstadoService } from '../../services/cotizacion-estado/cotizacion-estado.service';
import { TipoSolicitudService } from '../../services/tipo-solicitud/tipo-solicitud.service';
import { ConsultarAsesorByIDService } from '../../services/cotizador-contratante/consultar-asesor-by-id.service';
import { SnackBarService } from '../../utils/util.SnackBarService';

import { MaincontainerComponent } from '../maincontainer/maincontainer.component';
import { Base } from '../common/base';
import { DialogCerrarCotizacionComponent } from '../common/dialogs/dialog-cerrar-cotizacion/dialog-cerrar-cotizacion.component';
import { DialogSeguimientoCotizacionComponent } from '../common/dialogs/dialog-seguimiento-cotizacion/dialog-seguimiento-cotizacion.component';

import { LineaNegocio } from '../../models/cotizador-programas/lineas-negocio/consultar-lineas-negocio-response.model';
import { ProbabilidadCierre } from '../../models/probabilidad-cierre/probabilidad-cierre.model';
import { CotizacionEstado } from '../../models/cotizacion-estado/cotizacion-estado.model';
import { TipoSolicitud } from '../../models/tipo-solicitud/tipo-solicitud.model';
import { CotizacionFilter } from '../../models/mis-cotizaciones/cotizacion-filter.model';
import { Cotizacion as CotizacionAPI } from '../../models/mis-cotizaciones/cotizacion.model';

export interface Cotizacion {
  idCotizacion: number;
  numero_cotizacion: string;
  fecha: string;
  nombre_contratante: string;
  no_documento: string;
  tipo_solicitud: string;
  linea_negocio: string;
  estado: string;
  probabilidad_cierre: string;
  seguimiento: string;
  colorSemaforo: string; // "red" | "yellow" | "green" | "gray" | "none"
  idTipoCotizacion: number; // 1 = Individual, 2 = Colectivo Estándar
  idTipoSolicitud: number;
}



@Component({
  selector: 'app-mis-cotizaciones',
  templateUrl: './mis-cotizaciones.component.html',
  styleUrls: ['./mis-cotizaciones.component.css']
})

export class MisCotizacionesComponent extends Base implements OnDestroy {

  // Detección de dispositivo móvil
  isMobile: boolean = false;

  cotizacionSoloLectura(element?: any): boolean {
    const estado = (element?.estado ?? '').toString().trim().toUpperCase();
    return estado === 'ACEPTADA' || estado === 'CERRADA' || estado.startsWith('CERRADA ');
  }

  async openDialogCierreCotizacion(element?: any) {
    if (this.cotizacionSoloLectura(element)) {
      return;
    }

    console.log('[CierreCotizacion] element completo:', element);
    const authInfo = await this.authService.getAuthInfo();
    const docField: string = authInfo?.document || '';
    const usuarioCierre = docField.includes('.')
      ? docField.split('.')[1].trim()
      : docField;

    const idCotizacion = parseInt(element?.numero_cotizacion, 10) || 0;
    const idTipoCotizacion: number = element?.idTipoCotizacion ?? 1;

    const dialogRef = this.dialog.open(DialogCerrarCotizacionComponent, {
      width: '700px',
      data: {
        title: 'Cerrar Cotización',
        ventaGanadaLabel: 'Venta Ganada',
        idTipoCotizacion: idTipoCotizacion,
        idCotizacion: idCotizacion,
        usuarioCierre: usuarioCierre
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;
      if (result.ventaGanada === 'SI') {
        if (result.success === false) {
          this.showMessageDialog('Error', result.errorMessage || 'Error al enviar a radicación.', 'Aceptar', '', true, 1, this.dialog, () => { });
        } else {
          this.showMessageDialog('Éxito', result.message || 'La cotización fue enviada a radicación exitosamente.', 'Aceptar', '', true, 1, this.dialog, () => { });
          this.buscarCotizaciones();
        }
      } else if (result.ventaGanada === 'NO') {
        if (result.success === false) {
          this.showMessageDialog('Error', result.errorMessage || 'Error al cerrar la cotización.', 'Aceptar', '', true, 1, this.dialog, () => { });
        } else {
          this.showMessageDialog('Éxito', 'La cotización fue cerrada exitosamente.', 'Aceptar', '', true, 1, this.dialog, () => { });
          this.buscarCotizaciones();
        }
      }
    });
  }

  /**
   * Abre el dialog de seguimiento de cotización
   */
  openDialogSeguimiento(element?: any) {
    // Validar que no sea un estado ACEPTADA o CERRADA
    if (element?.estado?.toUpperCase() === 'ACEPTADA' || element?.estado?.toUpperCase() === 'CERRADA') {
      return; // No abrir dialog para estados finales
    }

    const dialogRef = this.dialog.open(DialogSeguimientoCotizacionComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        idCotizacion: parseInt(element?.numero_cotizacion, 10) || 0,
        numeroCotizacion: element?.numero_cotizacion || '',
        nombreCliente: element?.nombre_contratante || '',
        fechaCreacion: element?.fecha || '',
        probabilidadActual: element?.probabilidad_cierre || '',
        canalesActuales: [] // Se cargarán desde el servicio
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        console.log('Seguimiento guardado:', result);
        // Aquí iría la llamada al servicio para guardar el seguimiento
        // Recargar las cotizaciones después de guardar
        this.buscarCotizaciones();
      }
    });
  }


  // Controles de formulario
  toppings = new FormControl([]);
  tipoSolicitudControl = new FormControl([]);
  myQuoteDate: FormGroup;
  myQuoteSearch: FormGroup;

  // Datos de selectores
  toppingList: string[] = ['MI'];
  lineasNegocio: LineaNegocio[] = [];
  probabilidadesCierre: ProbabilidadCierre[] = [];
  probabilidadesList: string[] = [];
  estadosCotizacion: CotizacionEstado[] = [];
  estadosList: string[] = [];
  tiposSolicitud: TipoSolicitud[] = [];
  tiposSolicitudList: string[] = [];

  // Configuración de tabla
  displayedColumns: string[] = ['numero_cotizacion', 'fecha', 'nombre_contratante', 'no_documento', 'tipo_solicitud', 'linea_negocio', 'estado', 'probabilidad_cierre', 'seguimiento', 'acciones'];
  dataSource = new MatTableDataSource<Cotizacion>([]);

  // Configuración de paginación
  pageSizeOptions: number[] = [10, 25, 50, 100];
  length: number = 0;
  pageSize = 10;
  pageIndex = 0;
  pageEvent: PageEvent;

  // Filtros seleccionados
  selectedEstado: string = '';
  selectedProbabilidad: string = '';

  // Datos del asesor
  cedulaAsesor: string = '';
  idAsesor: number | null = null;
  nombreAsesor: string = '';
  correoAsesor: string = '';
  celularAsesor: string = '';
  ciudadAsesor: string = '';

  // Configuración de fechas
  minDate: Date;
  maxDate: Date;

  private readonly KEY_PARAMETROS_JASPER_STORAGE =
  'parametros_jasper_individual';

  private readonly KEY_PARAMETROS_JASPER_COLECTIVO_STORAGE =
  'parametros_jasper_colectivo';

  // Validadores
  public alphaNumeric: string | RegExp = new RegExp(/(^[a-zA-ZÀ-ÿ\u00f1\u00d1\d]+([a-zA-ZÀ-ÿ\u00f1\u00d1\s.\d])*)$/);

  @ViewChild(MatPaginator, { static: true })
  paginator!: MatPaginator;

  @ViewChild('paginatorMobile')
  paginatorMobile?: MatPaginator;

  getMinDateOrMaxDate(year: number): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() - year);
    return date;
  }

  equalFieldsCalendar(campo1: string, campo2: string) {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const calendar1 = formGroup.get(campo1)?.value;
      const calendar2 = formGroup.get(campo2)?.value;

      if (calendar1 && calendar2) {
        if (calendar1 > calendar2) {
          return { greaterTheStartDate: true };
        } else if (Math.floor(calendar2 / 86400000) > (Math.floor(calendar1 / 86400000) + 31)) {
          return { daysExceeded: true };
        }
      }
      return null;
    };
  }

  constructor(
    private networking: NetworkingMyQuotation,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private mainCmp: MaincontainerComponent,
    private authService: AuthService,
    private consultarLineasService: ConsultarLineasGruposProgramasService,
    private probabilidadCierreService: ProbabilidadCierreService,
    private cotizacionEstadoService: CotizacionEstadoService,
    private tipoSolicitudService: TipoSolicitudService,
    private consultarAsesorByIDService: ConsultarAsesorByIDService,
    private snackBarService: SnackBarService
  ) {
    super();
    
    // Detectar tamaño de pantalla
    this.checkScreenSize();
    
    // Inicializar configuración de fechas
    this.maxDate = this.getMinDateOrMaxDate(0);
    this.minDate = this.getMinDateOrMaxDate(100);
    
    // Inicializar formularios
    this.myQuoteDate = this.fb.group({ 
      fecha_inicio: [null],
      fecha_fin: [null]
    }, {
      validators: [this.equalFieldsCalendar('fecha_inicio', 'fecha_fin')]
    });

    this.myQuoteSearch = this.fb.group({
      searchEngine: [null, [Validators.pattern(this.alphaNumeric)]],
    });
    
    // Inicializar paginación
    this.pageEvent = {
      length: 0,
      pageIndex: 0,
      pageSize: 10,
      previousPageIndex: 0,
    };
  }


  ngAfterViewInit(): void {
    // Configurar listener para cambios de página
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
      this.buscarCotizaciones();
    });

    // Configurar listener para cambios de página (móvil)
    this.paginatorMobile?.page.subscribe((event: PageEvent) => {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
      this.buscarCotizaciones();
    });

    // Listener para cambios de tamaño de pantalla
    window.addEventListener('resize', () => this.checkScreenSize());

    setTimeout(() => {
      this.mainCmp.setTitle("Mis cotizaciones");
      // Respaldo: Cargar datos si aún no se han cargado
      if (this.idAsesor && this.dataSource.data.length === 0) {
        console.log('🔄 Cargando cotizaciones desde ngAfterViewInit (respaldo)');
        this.buscarCotizaciones();
      }
    }, 500);

    this.spinner.hide();
  }

  get startDateErrorsMsg(): string {
    if (this.myQuoteDate.hasError('greaterTheStartDate')) {
      return 'La fecha desde no debe ser superior a la fecha hasta';
    }
    return '';
  }

  get endDateErrorsMsg(): string {
    if (this.myQuoteDate.hasError('greaterTheStartDate')) {
      return 'La fecha hasta no debe ser inferior a la fecha desde';
    } else if (this.myQuoteDate.hasError('daysExceeded')) {
      return 'La consulta no puede ser mayor a 31 dias';
    }
    return '';
  }

  get searchEngineErrosMsg(): string {
    const errors = this.myQuoteSearch.get('searchEngine')?.errors;
    if (errors?.['pattern']) return 'No se permiten caracteres especiales';
    return '';
  }

  showErrorsDate(): boolean {
    if (this.myQuoteDate.invalid) {
      this.myQuoteDate.markAllAsTouched();
      return true;
    }
    return false;
  }

  showErrorsSearch(): boolean {
    if (this.myQuoteSearch.invalid) {
      this.myQuoteSearch.markAllAsTouched();
      return true;
    }
    return false;
  }

  fieldsValidatorsDate(campo: string): boolean {
    return !!(this.myQuoteDate.get(campo)?.invalid && this.myQuoteDate.get(campo)?.touched);
  }

  fieldsValidatorsSearch(campo: string): boolean {
    return !!(this.myQuoteSearch.get(campo)?.invalid && this.myQuoteSearch.get(campo)?.touched);
  }

  resetFilters(): void {
    // Limpiar formularios sin emitir eventos para evitar llamadas intermedias a buscarCotizaciones()
    this.myQuoteDate.get('fecha_inicio')?.setValue(null, { emitEvent: false });
    this.myQuoteDate.get('fecha_fin')?.setValue(null, { emitEvent: false });
    this.myQuoteDate.get('fecha_inicio')?.markAsUntouched();
    this.myQuoteDate.get('fecha_fin')?.markAsUntouched();
    this.myQuoteSearch.get('searchEngine')?.setValue(null, { emitEvent: false });

    // Limpiar selecciones
    this.selectedEstado = '';
    this.selectedProbabilidad = '';
    this.toppings.reset([]);
    this.tipoSolicitudControl.reset([]);

    // Reiniciar paginación
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;
    this.pageIndex = 0;
    this.pageSize = 10;

    // Cargar cotizaciones sin filtros
    this.buscarCotizaciones();
  }


  async ngOnInit(): Promise<void> {
    // Obtener cédula del asesor (debe completarse antes de cargar datos)
    await this.obtenerCCAsesor();
    this.cargarDatosAsesor();

    // Cargar líneas de negocio
    this.cargarLineasNegocio();

    // Cargar probabilidades de cierre
    this.cargarProbabilidadesCierre();

    // Cargar estados de cotización
    this.cargarEstadosCotizacion();

    // Cargar tipos de solicitud
    this.cargarTiposSolicitud();

    // Configurar listeners para cambios de fecha
    this.configurarValidacionFechas();

    // Configurar paginador
    this.paginator._intl.itemsPerPageLabel = 'Registros por página';
    this.paginator._intl.nextPageLabel = 'Página siguiente';
    this.paginator._intl.previousPageLabel = 'Página anterior';
    this.paginator._intl.firstPageLabel = 'Primera página';
    this.paginator._intl.lastPageLabel = 'Última página';
    this.paginator._intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) return `0 de ${length}`;
      const totalPages = Math.ceil(length / pageSize);
      return `Página ${page + 1} de ${totalPages}`;
    };
    this.paginator._intl.changes.next();

    // Cargar cotizaciones después de obtener el idAsesor
    if (this.idAsesor) {
      console.log('✅ idAsesor obtenido en ngOnInit, cargando cotizaciones...');
      this.buscarCotizaciones();
    } else {
      console.warn('⚠️ No se pudo obtener idAsesor en ngOnInit');
    }
  }

  /**
   * Configura los listeners de validación para las fechas
   */
  private configurarValidacionFechas(): void {
    this.myQuoteDate.get('fecha_inicio')?.valueChanges.subscribe(() => {
      this.myQuoteDate.get('fecha_inicio')?.markAsTouched();
      this.myQuoteDate.get('fecha_fin')?.markAsTouched();
      this.myQuoteDate.updateValueAndValidity();

      if (this.myQuoteDate.valid && this.idAsesor) {
        this.buscarCotizaciones(true);
      }
    });

    this.myQuoteDate.get('fecha_fin')?.valueChanges.subscribe(() => {
      this.myQuoteDate.get('fecha_inicio')?.markAsTouched();
      this.myQuoteDate.get('fecha_fin')?.markAsTouched();
      this.myQuoteDate.updateValueAndValidity();

      if (this.myQuoteDate.valid && this.idAsesor) {
        this.buscarCotizaciones(true);
      }
    });
  }

  /**
   * Obtiene la cédula del asesor logueado y luego consulta su idAsesor
   * @returns Promise con el número de cédula del asesor
   */
  async obtenerCCAsesor(): Promise<string> {
    try {
      const authInfo = await this.authService.getAuthInfo();
      const documentUser = authInfo.document;
      console.log('Información de autenticación obtenida:', authInfo);

      // Extraer la cédula del documento
      this.cedulaAsesor = documentUser.split('.')[1].trim();
      console.log('📝 Documento asesor:', this.cedulaAsesor);

      // Obtener el idAsesor usando el servicio
      this.idAsesor = await this.networking.obtenerIdAsesorPorDocumento(this.cedulaAsesor);
      console.log('✅ ID Asesor obtenido:', this.idAsesor);

      return this.cedulaAsesor;

    } catch (error) {
      console.error('❌ Error al obtener la información de autenticación:', error);
      return this.cedulaAsesor;
    }
  }

  /**
   * Obtiene los datos del asesor para la generación del PDF individual.
   */
  private cargarDatosAsesor(): void {
    if (!this.idAsesor) {
      return;
    }

    const request = {
      nombreClave: 'getSpCotConsultarAsesorPorID',
      jsonParametros: JSON.stringify({
        prm_id_asesor: String(this.idAsesor)
      })
    };

    this.consultarAsesorByIDService.consultarAsesorByID(request).subscribe({
      next: (response) => {
        if (response?.status && Array.isArray(response.data) && response.data.length > 0) {
          const asesor = response.data[0];
          this.nombreAsesor = (asesor?.NombreCompleto ?? '').toString().trim();
          this.correoAsesor = (asesor?.email ?? '').toString().trim();
          this.celularAsesor = (asesor?.telefono ?? '').toString().trim();
          this.ciudadAsesor = this.capitalizeFirstLetter(
            (asesor?.ciudad ?? '').toString().trim()
          );
        }
      },
      error: (error) => {
        console.error('Error al consultar los datos del asesor:', error);
      }
    });
  }

  /**
   * Detecta si está en dispositivo móvil
   */
  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  /**
   * Limpia el estado del componente al destruirse
   */
  ngOnDestroy(): void {
    console.log('🧹 Limpiando componente mis-cotizaciones');
    
    // Remover listener de resize
    window.removeEventListener('resize', () => this.checkScreenSize());
    // Limpiar variables del componente
    this.idAsesor = null;
    this.cedulaAsesor = '';

    // Limpiar tabla
    this.dataSource.data = [];

    // Resetear filtros
    this.selectedEstado = '';
    this.selectedProbabilidad = '';
    this.toppings.reset([]);
    this.tipoSolicitudControl.reset([]);
  }

  private capitalizeFirstLetter(value: string): string {
    const trimmed = (value ?? '').toString().trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  }

  /**
   * Carga las líneas de negocio desde el servicio.
   * Extrae el código corto haciendo split por '-' (ej: "SAO-SALUD ORAL" → "SAO")
   */
  cargarLineasNegocio(): void {
    this.consultarLineasService.consultarLineasNegocio().subscribe({
      next: (response) => {
        if (response.status && response.data?.jsonRespuesta) {
          this.lineasNegocio = response.data.jsonRespuesta;
          const codigoMap: { [key: string]: string } = { 'SMP': 'MI' };
          this.toppingList = this.lineasNegocio
            .map(l => {
              const codigo = l.linea_negocio.match(/[A-Z]+/)?.[0] ?? l.linea_negocio.trim();
              return codigoMap[codigo] ?? codigo;
            })
            .filter(codigo => codigo === 'MI');
          console.log('Líneas de negocio cargadas:', this.toppingList);
        }
      },
      error: (error) => {
        console.error('Error al cargar líneas de negocio:', error);
        this.toppingList = ['MI'];
      }
    });
  }

  /**
   * Carga los estados de cotización desde el servicio
   */
  cargarEstadosCotizacion(): void {
    this.cotizacionEstadoService.getEstadosCotizacion().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.estadosCotizacion = response.data.filter(est => est.activo === 1);
          this.estadosList = this.estadosCotizacion.map(est => est.nombre);
          console.log('Estados de cotización cargados:', this.estadosCotizacion);
        }
      },
      error: (error) => {
        console.error('Error al cargar estados de cotización:', error);
        // Fallback a valores por defecto en caso de error
        this.estadosList = ['INCOMPLETA', 'GUARDADA', 'ENVIADA', 'ACEPTADA', 'CERRADA'];
      }
    });
  }

  /**
   * Carga los tipos de solicitud desde el servicio
   */
  cargarTiposSolicitud(): void {
    this.tipoSolicitudService.getTiposSolicitud().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.tiposSolicitud = response.data
            .filter(t => t.activo === 'S')
            // Nuevo colectivo se oculta del filtro. Las cotizaciones ya creadas siguen viéndose.
            .filter(t => {
              const nombre = (t.nombreTipoSolicitud || '').toString().trim().toUpperCase();
              return Number(t.idTipoSolicitud) !== 3
                && nombre !== 'NUEVO COLECTIVO'
                && nombre !== 'COLECTIVO NUEVO';
            });
          this.tiposSolicitudList = this.tiposSolicitud.map(t => t.nombreTipoSolicitud);
          console.log('Tipos de solicitud cargados:', this.tiposSolicitud);
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos de solicitud:', error);
        this.tiposSolicitudList = [/*'NUEVO COLECTIVO', */'VENTA NUEVA', 'INCLUSIÓN'];
      }
    });
  }

  /**
   * Carga las probabilidades de cierre desde el servicio
   */
  cargarProbabilidadesCierre(): void {
    this.probabilidadCierreService.getProbabilidadesCierre().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.probabilidadesCierre = response.data.filter(prob => prob.activo === 1);
          // Actualizar probabilidadesList con los nombres de las probabilidades
          this.probabilidadesList = this.probabilidadesCierre.map(prob => prob.nombre);
          console.log('Probabilidades de cierre cargadas:', this.probabilidadesCierre);
        }
      },
      error: (error) => {
        console.error('Error al cargar probabilidades de cierre:', error);
        // Fallback a valores por defecto en caso de error
        this.probabilidadesList = ['Alta', 'Media', 'Baja'];
      }
    });
  }
  
  private obtenerParametrosJasperGuardados(
    idCotizacion: number | string
  ): any | null {
    const parametrosStorage = localStorage.getItem(
      this.KEY_PARAMETROS_JASPER_STORAGE
    );

    if (!parametrosStorage) {
      return null;
    }

    try {
      const data = JSON.parse(parametrosStorage);

      if (!data || typeof data !== 'object') {
        return null;
      }

      const parametrosCotizacion =
        data[String(idCotizacion)];

      if (
        !parametrosCotizacion ||
        typeof parametrosCotizacion !== 'object'
      ) {
        return null;
      }

      return parametrosCotizacion;
    } catch (error) {
      console.error(
        'Error leyendo parámetros Jasper:',
        error
      );

      return null;
    }
  }

  private obtenerParametrosColectivoParaJasper(
    idCotizacion: number | string
  ): {
    porcentajeSubsidio: string;
    mostrarTodosEscenarios: boolean;
    aplicaAnexos: boolean;
    nombreJefe: string;
    cargoJefe: string;
  } {
    const parametrosStorage = localStorage.getItem(
      this.KEY_PARAMETROS_JASPER_COLECTIVO_STORAGE
    );

    const valoresPorDefecto = {
      porcentajeSubsidio: '0',
      mostrarTodosEscenarios: false,
      aplicaAnexos: false,
      nombreJefe: this.nombreAsesor?.trim() || '',
      cargoJefe: ''
    };

    if (!parametrosStorage) {
      return valoresPorDefecto;
    }

    try {
      const data = JSON.parse(parametrosStorage);

      if (!data || typeof data !== 'object') {
        return valoresPorDefecto;
      }

      const parametrosCotizacion = data[String(idCotizacion)];

      if (
        !parametrosCotizacion ||
        typeof parametrosCotizacion !== 'object'
      ) {
        return valoresPorDefecto;
      }

      return {
        porcentajeSubsidio: String(
          parametrosCotizacion.pPorcentajeSubsidio ?? '0'
        ),

        mostrarTodosEscenarios:
          parametrosCotizacion.pMostrarTodosEscenarios === true ||
          parametrosCotizacion.pMostrarTodosEscenarios === 'true' ||
          parametrosCotizacion.pMostrarTodosEscenarios === '1',

        aplicaAnexos:
          parametrosCotizacion.pAplicaAnexos === true ||
          parametrosCotizacion.pAplicaAnexos === 'true' ||
          parametrosCotizacion.pAplicaAnexos === '1',

        nombreJefe: String(
          parametrosCotizacion.pNombreJefe ??
          this.nombreAsesor ??
          ''
        ).trim(),

        cargoJefe: String(
          parametrosCotizacion.pCargoJefe ?? ''
        ).trim()
      };
    } catch (error) {
      console.error(
        'Error leyendo parámetros Jasper colectivo:',
        error
      );

      return valoresPorDefecto;
    }
  }

  private obtenerPCuotaInscripcionParaJasper(
    idCotizacion: number | string
  ): string {
    const parametros =
      this.obtenerParametrosJasperGuardados(
        idCotizacion
      );

    return parametros?.pCuotaInscripcion === '1'
      ? '1'
      : '0';
  }

  private obtenerPPagoProporcionalParaJasper(
    idCotizacion: number | string
  ): string {
    const parametros =
      this.obtenerParametrosJasperGuardados(
        idCotizacion
      );

    return parametros
      ?.pPagoProporcionalAnticipado === '1'
        ? '1'
        : '0';
  }

  private obtenerPPrecioBaseAnexosParaJasper(
    idCotizacion: number | string
  ): string {
    const parametros =
      this.obtenerParametrosJasperGuardados(
        idCotizacion
      );

    return parametros?.pPrecioBaseAnexos === '1'
      ? '1'
      : '0';
  }

  private obtenerPComparativoParaJasper(): string {
    const compararStorage = localStorage.getItem('rq006_comparar_programas_por_linea');

    if (!compararStorage) {
      return '0';
    }

    try {
      const data = JSON.parse(compararStorage);

      if (!data || typeof data !== 'object') {
        return '0';
      }

      const tieneDosProgramasSeleccionados = Object.values(data).some((programas: any) => {
        return Array.isArray(programas) && programas.length >= 2;
      });

      return tieneDosProgramasSeleccionados ? '1' : '0';
    } catch (error) {
      console.error('Error leyendo selección de comparar:', error);
      return '0';
    }
  }

  private obtenerPTarifaAsociadoParaJasper(
    idCotizacion: number | string
  ): string {
    const parametros =
      this.obtenerParametrosJasperGuardados(
        idCotizacion
      );

    return parametros?.pTarifaAsociado === '1'
      ? '1'
      : '0';
  }

  private crearPdfIndividualInlineUrl(
    idCotizacion: number,
    pComparativo: string,
    pCuotaInscripcion: string,
    pPagoProporcionalAnticipado: string,
    pPrecioBaseAnexos: string,
    pTarifaAsociado: string
  ): string {
    if (!idCotizacion) {
      return 'about:blank';
    }

    const nombreArchivo =
      `cotización_${idCotizacion}.pdf`;

    const params = new URLSearchParams({
      idCotizacion:
        idCotizacion.toString(),

      nombreAsesor:
        this.nombreAsesor.trim(),

      telefonoAsesor:
        this.celularAsesor.trim(),

      correoAsesor:
        this.correoAsesor.trim(),

      comparativo:
        pComparativo,

      cuotaInscripcion:
        pCuotaInscripcion,

      pagoProporcionalAnticipado:
        pPagoProporcionalAnticipado,

      precioBaseAnexos:
        pPrecioBaseAnexos,

      tarifaAsociado:
        pTarifaAsociado,

      ciudadAsesor:
        this.ciudadAsesor ?? ''
    });

    return (
      `${environment.HOST}` +
      `${URL_MAP.API.PDF_COTIZACION_INDIVIDUAL}/` +
      `${nombreArchivo}?${params.toString()}`
    );
  }

  private crearPdfColectivoInlineUrl(
    idCotizacion: number,
    porcentajeSubsidio: string,
    mostrarTodosEscenarios: boolean,
    aplicaAnexos: boolean,
    nombreJefe: string,
    cargoJefe: string
  ): string {
    const nombreArchivo =
      `cotización_${idCotizacion}.pdf`;

    const params = new URLSearchParams({
      idCotizacion: idCotizacion.toString(),
      nombreAsesor: this.nombreAsesor.trim(),
      telefonoAsesor: this.celularAsesor.trim(),
      correoAsesor: this.correoAsesor.trim(),
      ciudadAsesor: this.ciudadAsesor || '',
      porcentajeSubsidio:
        porcentajeSubsidio || '0',
      mostrarTodosEscenarios:
        String(mostrarTodosEscenarios),
      aplicaAnexos:
        String(aplicaAnexos),
      nombreJefe:
        nombreJefe || '',
      cargoJefe:
        cargoJefe || ''
    });

    return (
      `${environment.HOST}` +
      `/cotizador/api/certificados/pdf/cotizacolectivo/` +
      `${encodeURIComponent(nombreArchivo)}?` +
      params.toString()
    );
  }

  /**
   * Abre el PDF de la cotización en una nueva pestaña del navegador.
   * Usa el mismo endpoint para individual y colectivo de forma temporal.
   * TODO: reemplazar documento y anio por los valores reales del contratante cuando el endpoint definitivo esté disponible.
   */
  viewQuote(data: Cotizacion): void {
    console.log('🖨️ Generando PDF para la cotización:', data.numero_cotizacion);
    const esNuevoColectivo = Number(data.idTipoSolicitud) === 3;
    if (!esNuevoColectivo && (!this.nombreAsesor || !this.celularAsesor || !this.correoAsesor)) {
      this.snackBarService.alert('btn-danger', 'No se encontraron los datos del asesor para generar el PDF.');
      return;
    }

    const idCotizacionFromRow = Number.parseInt((data.numero_cotizacion ?? '').toString(), 10);
    const idCotizacion = Number.isFinite(idCotizacionFromRow) && idCotizacionFromRow > 0
      ? idCotizacionFromRow
      : data.idCotizacion;

    this.spinner.show();

    const pComparativo = this.obtenerPComparativoParaJasper();
    const pCuotaInscripcion = this.obtenerPCuotaInscripcionParaJasper(idCotizacion);
    const pPagoProporcionalAnticipado = this.obtenerPPagoProporcionalParaJasper(idCotizacion);
    const pPrecioBaseAnexos = this.obtenerPPrecioBaseAnexosParaJasper(idCotizacion);
    const pTarifaAsociado =
    this.obtenerPTarifaAsociadoParaJasper(
      idCotizacion
    );
    const parametrosColectivo =this.obtenerParametrosColectivoParaJasper(idCotizacion);

    console.log(
      'PARÁMETROS JASPER MIS COTIZACIONES:',
      {
        idCotizacion,
        idTipoCotizacion: data.idTipoCotizacion,
        pComparativo,
        pCuotaInscripcion,
        pPagoProporcionalAnticipado,
        pPrecioBaseAnexos,
        pTarifaAsociado,
        porcentajeSubsidio:
          parametrosColectivo.porcentajeSubsidio,
        mostrarTodosEscenarios:
          parametrosColectivo.mostrarTodosEscenarios,
        aplicaAnexos:
          parametrosColectivo.aplicaAnexos,
        nombreJefe:
          parametrosColectivo.nombreJefe,
        cargoJefe:
          parametrosColectivo.cargoJefe
      }
    );

    if (!esNuevoColectivo) {
      const pdfUrl = this.crearPdfIndividualInlineUrl(
        idCotizacion,
        pComparativo,
        pCuotaInscripcion,
        pPagoProporcionalAnticipado,
        pPrecioBaseAnexos,
        pTarifaAsociado
      );
      this.spinner.hide();
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const pdfColectivoUrl =
      this.crearPdfColectivoInlineUrl(
        idCotizacion,
        parametrosColectivo.porcentajeSubsidio,
        parametrosColectivo.mostrarTodosEscenarios,
        parametrosColectivo.aplicaAnexos,
        parametrosColectivo.nombreJefe,
        parametrosColectivo.cargoJefe
      );
    this.spinner.hide();
    window.open(pdfColectivoUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Inicia la edición de una cotización: llama a iniciar-edicion (que crea versión si estado=ENVIADA),
   * guarda los datos en localStorage y navega al cotizador.
   */
  editQuote(data: Cotizacion): void {
    if (this.cotizacionSoloLectura(data)) {
      return;
    }

    this.spinner.show();
    console.log('📝 Iniciando edición de la cotización:', data.numero_cotizacion);

    this.networking.iniciarEdicion(data.numero_cotizacion).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Validar que la cotización sea editable
          if (response.data.esEditable === false) {
            this.spinner.hide();
            this.showMessageDialog(
              "Información",
              "Esta cotización no se puede editar en su estado actual.",
              "Aceptar",
              "",
              true,
              2,
              this.dialog,
              () => { }
            );
            return;
          }
          localStorage.setItem('cotizacionEdicion', JSON.stringify(response.data));
          console.log('✅ Cotización guardada para edición:', response.data);
          this.router.navigateByUrl('/main/cotizador');
        }
        this.spinner.hide();
      },
      error: (error) => {
        this.spinner.hide();
        console.error('❌ Error al iniciar la edición de la cotización:', error);
        this.showMessageDialog(
          "Error",
          error || "Error al cargar los detalles de la cotización",
          "Aceptar",
          "",
          true,
          2,
          this.dialog,
          () => { }
        );
      }
    });
  }

  // ===================================================================
  // EXPORTACIÓN
  // ===================================================================

  /**
   * Exporta las cotizaciones aplicando los filtros actuales
   */
  exportarCotizaciones(): void {
    console.log('======== EXPORTAR COTIZACIONES ========');
    console.log('idAsesor:', this.idAsesor);
    console.log('cedulaAsesor:', this.cedulaAsesor);
    console.log('selectedEstado:', this.selectedEstado);
    console.log('selectedProbabilidad:', this.selectedProbabilidad);
    console.log('searchValue:', this.myQuoteSearch.get('searchEngine')?.value);

    // URL base del endpoint de exportar
    let url = `${environment.HOST}${URL_MAP.API.COTIZADOR_COTIZACION_EXPORTAR}?`;
    const params: string[] = [];

    // Agregar idAsesor si está disponible
    if (this.idAsesor) {
      params.push(`idAsesor=${this.idAsesor}`);
      console.log('idAsesor usado:', this.idAsesor);
    }

    // Agregar búsqueda general si hay texto
    const searchValue = this.myQuoteSearch.get('searchEngine')?.value;
    if (searchValue && searchValue.trim()) {
      params.push(`busqueda=${encodeURIComponent(searchValue.trim())}`);
      console.log('Búsqueda:', searchValue.trim());
    }

    // Agregar fechas si están presentes
    const fechaInicio = this.myQuoteDate.get('fecha_inicio')?.value;
    const fechaFin = this.myQuoteDate.get('fecha_fin')?.value;

    if (fechaInicio) {
      const fechaFormateada = this.formatDateToDDMMYYYY(fechaInicio);
      params.push(`fechaDesde=${fechaFormateada}`);
      console.log('Fecha Desde:', fechaFormateada);
    }

    if (fechaFin) {
      const fechaFormateada = this.formatDateToDDMMYYYY(fechaFin);
      params.push(`fechaHasta=${fechaFormateada}`);
      console.log('Fecha Hasta:', fechaFormateada);
    }

    // Agregar estado si está seleccionado
    if (this.selectedEstado) {
      const estadoId = this.mapEstadoToId(this.selectedEstado);
      if (estadoId) {
        params.push(`estado=${estadoId}`);
        console.log('Estado ID:', estadoId, '(' + this.selectedEstado + ')');
      }
    }

    // Agregar probabilidad de cierre si está seleccionada
    if (this.selectedProbabilidad) {
      const probabilidadId = this.getProbabilidadCierreId(this.selectedProbabilidad);
      if (probabilidadId) {
        params.push(`idProbabilidadCierre=${probabilidadId}`);
        console.log('Probabilidad ID:', probabilidadId, '(' + this.selectedProbabilidad + ')');
      }
    }

    // Agregar tipos de solicitud seleccionados
    const tiposSolicitudExportar: any[] = this.tipoSolicitudControl.value || [];

    tiposSolicitudExportar.forEach(tipo => {
      const tipoSolicitudId = typeof tipo === 'number'
        ? tipo
        : this.getTipoSolicitudId(tipo);

      if (tipoSolicitudId) {
        params.push(`idTipoSolicitud=${tipoSolicitudId}`);
        console.log('Tipo solicitud ID:', tipoSolicitudId);
      }
    });

    // Agregar líneas de negocio seleccionadas
    const lineasExportar: string[] = this.toppings.value || [];
    lineasExportar.forEach(ln => params.push(`lineaNegocio=${encodeURIComponent(ln)}`));

    // Construir URL final
    url += params.join('&');

    console.log('URL COMPLETA de exportación:', url);
    console.log('Parámetros:', params);
    console.log('========================================');

    // Abrir la URL en una nueva ventana/tab para descargar el archivo
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Formatea una fecha de Date a string en formato DD-MM-YYYY
   */
  private formatDateToDDMMYYYY(date: Date): string {
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const anio = date.getFullYear();
    return `${dia}-${mes}-${anio}`;
  }

  /**
   * Mapea el nombre del estado al ID del estado
   * Estados disponibles:
   * - INCOMPLETA: 1
   * - GUARDADA: 2
   * - ENVIADA: 3
   * - ACEPTADA: 4
   * - CERRADA: 5
   */
  private mapEstadoToId(estado: string): number | undefined {
    const found = this.estadosCotizacion.find(
      e => e.nombre.toUpperCase() === estado.toUpperCase()
    );
    return found?.idCotizacionEstado;
  }

  /**
   * Obtiene el ID de probabilidad de cierre por nombre
   */
  private getProbabilidadCierreId(nombre: string): number | undefined {
    const probabilidad = this.probabilidadesCierre.find(
      p => p.nombre.toUpperCase() === nombre.toUpperCase()
    );
    return probabilidad?.idProbabilidadCierre;
  }

  /**
   * Obtiene el ID de tipo de solicitud por nombre
   */
  private getTipoSolicitudId(nombre: string): number | undefined {
    const tipo = this.tiposSolicitud.find(
      t => t.nombreTipoSolicitud.toUpperCase() === nombre.toUpperCase()
    );
    return tipo?.idTipoSolicitud;
  }

  private resetPageForFilter(): void {
    this.pageIndex = 0;
    this.paginator?.firstPage();
    this.paginatorMobile?.firstPage();
  }

  /**
   * Mapea una cotización del API al formato de la tabla local
   */
  private mapCotizacionAPIToLocal(cotizacion: CotizacionAPI): Cotizacion {
    // Formatear fecha de ISO a DD/MM/YYYY
    const fechaISO = new Date(cotizacion.fechaCreacion);
    const fechaFormateada = `${String(fechaISO.getDate()).padStart(2, '0')}/${String(fechaISO.getMonth() + 1).padStart(2, '0')}/${fechaISO.getFullYear()}`;

    // Determinar tipo de solicitud basado en idTipoSolicitud
    let tipoSolicitud = 'VENTA NUEVA';
    if (cotizacion.idTipoSolicitud === 1) {
      tipoSolicitud = 'VENTA NUEVA';
    } else if (cotizacion.idTipoSolicitud === 2) {
      tipoSolicitud = 'INCLUSIÓN';
    } else if (cotizacion.idTipoSolicitud === 3) {
      tipoSolicitud = 'NUEVO COLECTIVO';
    }

    // Obtener nombre del contratante y documento
    const nombreContratante = cotizacion.nombreContratante || 'N/A';
    const numeroDocumento = cotizacion.numeroDocumentoContratante || 'N/A';

    // Formatear fecha de seguimiento según el caso
    let fechaSeguimiento = 'SIN SEGUIMIENTO'; // Por defecto, si no hay fecha

    // Si el estado es ACEPTADA o CERRADA, mostrar NO APLICA
    if (cotizacion.nombreEstado?.toUpperCase() === 'ACEPTADA' ||
        cotizacion.nombreEstado?.toUpperCase() === 'CERRADA') {
      fechaSeguimiento = 'NO APLICA';
    } else if (cotizacion.fechaProxContacto) {
      // Si hay fecha próximo contacto, formatearla directamente del string (evitar problemas de zona horaria)
      // Formato esperado del API: "YYYY-MM-DD"
      const partesFecha = cotizacion.fechaProxContacto.split('-');
      if (partesFecha.length === 3) {
        const anio = partesFecha[0];
        const mes = partesFecha[1];
        const dia = partesFecha[2].split('T')[0]; // Eliminar la parte de hora si existe
        fechaSeguimiento = `${dia}/${mes}/${anio}`;
      }
    }

    return {
      idCotizacion: cotizacion.idCotizacion,
      numero_cotizacion: cotizacion.idCotizacion.toString(),
      fecha: fechaFormateada,
      nombre_contratante: nombreContratante,
      no_documento: numeroDocumento,
      tipo_solicitud: tipoSolicitud,
      linea_negocio: cotizacion.lineasNegocio?.join(', ') || 'N/A',
      estado: cotizacion.nombreEstado || 'N/A',
      probabilidad_cierre: cotizacion.nombreProbabilidadCierre || 'N/A',
      seguimiento: fechaSeguimiento,
      colorSemaforo: cotizacion.colorSemaforo || 'none',
      idTipoCotizacion: cotizacion.idTipoCotizacion ?? (cotizacion.idTipoSolicitud === 3 ? 2 : 1),
      idTipoSolicitud: Number(cotizacion.idTipoSolicitud),
    };
  }

  /**
   * Busca cotizaciones con los filtros aplicados
   * @param incluirBuscador Si es true, incluye el buscador general en los filtros
   */
  buscarCotizaciones(resetPage: boolean = false): void {
    if (resetPage) {
      this.resetPageForFilter();
    }

    // Validar que tenemos el ID del asesor
    if (!this.idAsesor) {
      console.warn('No se ha obtenido el ID del asesor, reintentando...');
      setTimeout(() => {
        if (this.idAsesor) {
          this.buscarCotizaciones(resetPage);
        }
      }, 1000);
      return;
    }

    // Validar formularios
    if (this.myQuoteDate.invalid) {
      this.myQuoteDate.markAllAsTouched();
      return;
    }

    this.spinner.show();

    console.log('🔍 Buscando cotizaciones con idAsesor:', this.idAsesor);

    // Construir filtros
    const filter: CotizacionFilter = {
      asesorId: this.idAsesor.toString(),
      page: this.pageIndex,
      size: this.pageSize
    };

    // Incluir búsqueda general si hay valor en el campo
    const searchValue = this.myQuoteSearch.get('searchEngine')?.value;
    if (searchValue && searchValue.trim()) {
      filter.busqueda = searchValue.trim();
    }

    // Agregar fechas si están presentes
    const fechaInicio = this.myQuoteDate.get('fecha_inicio')?.value;
    const fechaFin = this.myQuoteDate.get('fecha_fin')?.value;

    if (fechaInicio) {
      filter.fechaDesde = this.formatDateToDDMMYYYY(fechaInicio);
    }

    if (fechaFin) {
      filter.fechaHasta = this.formatDateToDDMMYYYY(fechaFin);
    }

    // Agregar estado si está seleccionado
    if (this.selectedEstado) {
      const estadoId = this.mapEstadoToId(this.selectedEstado);
      console.log('[Estado] nombre:', this.selectedEstado, '→ id enviado al backend:', estadoId);
      filter.estado = estadoId;
    }

    // Agregar probabilidad de cierre si está seleccionada
    if (this.selectedProbabilidad) {
      filter.idProbabilidadCierre = this.getProbabilidadCierreId(this.selectedProbabilidad);
    }

    // Agregar tipos de solicitud seleccionados
    const tiposSolicitudSeleccionados: number[] = this.tipoSolicitudControl.value || [];
    if (tiposSolicitudSeleccionados.length > 0) {
      filter.idTipoSolicitud = tiposSolicitudSeleccionados;
    }

    // Agregar líneas de negocio seleccionadas
    const lineasSeleccionadas: string[] = this.toppings.value || [];
    if (lineasSeleccionadas.length > 0) {
      filter.lineaNegocio = lineasSeleccionadas;
    }

    console.log('Filtros aplicados:', filter);

    // Llamar al servicio
    this.networking.getCotizacionesByAsesor(filter).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Mapear cotizaciones del API al formato local
          const cotizacionesLocales = response.data.contenido.map(
            cotizacion => this.mapCotizacionAPIToLocal(cotizacion)
          );

          // Actualizar tabla
          this.dataSource.data = cotizacionesLocales;
          this.length = response.data.totalElementos;
          this.pageSize = response.data.tamanioPagina;
          this.pageIndex = response.data.paginaActual;

          console.log('Cotizaciones cargadas:', cotizacionesLocales);
        }
        this.spinner.hide();
      },
      error: (error) => {
        this.spinner.hide();
        this.showMessageDialog(
          "Error",
          error || "Error al cargar las cotizaciones",
          "Aceptar",
          "",
          true,
          2,
          this.dialog,
          () => { }
        );
      }
    });
  }



}
